import { useEffect, useRef, useState } from "react";

// Frozen frame shown when the user prefers reduced motion — near the end of
// the loop so the mosaic, territory and pin are all settled and visible.
const REDUCED_MOTION_T = 0.82;

function prefersReducedMotion() {
  return (
    typeof window !== "undefined" &&
    window.matchMedia?.("(prefers-reduced-motion: reduce)").matches
  );
}

/**
 * Drives the hero map animation: returns t ∈ [0, 1) looping over `durationMs`
 * via requestAnimationFrame. If the user prefers reduced motion, returns a
 * fixed end-state frame and never starts the loop.
 */
export function useHeroLoop(durationMs = 10000): number {
  const [t, setT] = useState(0);
  const reduced = useRef(prefersReducedMotion());

  useEffect(() => {
    if (reduced.current) {
      setT(REDUCED_MOTION_T);
      return;
    }
    let raf: number;
    let start: number | null = null;
    const tick = (now: number) => {
      if (start == null) start = now;
      const elapsed = (now - start) % durationMs;
      setT(elapsed / durationMs);
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [durationMs]);

  return t;
}
